import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import ReusableTable from '../components/ReusableTable';
import CalificarTareaButton from '../components/CalificarTareaButton';
import ComentarioButton from '../components/ComentarioButton';
import './HomePage.css';
import { jwtDecode } from 'jwt-decode';
import { getEstadisticasTareas, getTareasConComentariosNoLeidos, getTareaById } from '../services/tareasService';
import { formatearBogota } from '../utils/fecha';

// Función para verificar si el usuario es administrador
const esAdmin = () => {
  const token = localStorage.getItem('authToken');
  if (!token) return false;
  try {
    const decodedToken = jwtDecode(token);
    return decodedToken.rolId === '1';
  } catch (error) {
    console.error("Error decodificando el token:", error);
    return false;
  }
};

// Función para verificar si el usuario es de soporte técnico
const esSoporteTecnico = () => {
  const token = localStorage.getItem('authToken');
  if (!token) return false;
  try {
    const decodedToken = jwtDecode(token);
    return decodedToken.rolId === '3';
  } catch (error) {
    console.error("Error decodificando el token:", error);
    return false;
  }
};

// Función para obtener el userId del token
const getUserId = () => {
  const token = localStorage.getItem('authToken');
  if (!token) return null;
  try {
    const decodedToken = jwtDecode(token);
    return decodedToken.userId;
  } catch (error) {
    console.error("Error decodificando el token:", error);
    return null;
  }
};

const HomePage = () => {
  const navigate = useNavigate();
  const [userName] = useState('Usuario');
  const [estadisticas, setEstadisticas] = useState({
    totalTareas: 0,
    tareasEstadoFinalizado: 0,
    tareasConEstadoCero: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModalCalificables, setShowModalCalificables] = useState(false);
  const [showModalComentarios, setShowModalComentarios] = useState(false);
  const [tareasConComentarios, setTareasConComentarios] = useState([]);
  const [loadingComentarios, setLoadingComentarios] = useState(false);
  const [showModalDetalleTarea, setShowModalDetalleTarea] = useState(false);
  const [detalleTarea, setDetalleTarea] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState(null);

  useEffect(() => {
    const fetchEstadisticas = async () => {
      const userId = getUserId();
      if (!userId) {
        setError('No se pudo obtener el ID del usuario. Por favor, inicie sesión de nuevo.');
        setLoading(false);
        return;
      }

      const response = await getEstadisticasTareas(userId);
      if (response.success) {
        setEstadisticas(response.data);
      } else {
        setError(response.message);
      }
      setLoading(false);
    };

    fetchEstadisticas();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    // Considera también limpiar los datos del usuario del localStorage
    // localStorage.removeItem('user');
    navigate('/auth/login');
  };

  const handleTareasConComentarios = async () => {
    const userId = getUserId();
    if (!userId) {
      setError('No se pudo obtener el ID del usuario. Por favor, inicie sesión de nuevo.');
      return;
    }

    setLoadingComentarios(true);
    setError(null);
    
    // Siempre abrir el modal primero
    setShowModalComentarios(true);
    
    try {
      const response = await getTareasConComentariosNoLeidos(userId);
      if (response.success) {
        setTareasConComentarios(response.data);
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError('Error al cargar las tareas con comentarios: ' + error.message);
    } finally {
      setLoadingComentarios(false);
    }
  };

  // Abrir modal de detalle al hacer clic en una tarjeta (ID tarea)
  const handleClickTareaId = async (idTarea) => {
    setShowModalDetalleTarea(true);
    setLoadingDetalle(true);
    setErrorDetalle(null);
    setDetalleTarea(null);

    const resp = await getTareaById(idTarea);
    if (resp.success) {
      // Adaptar DTO del backend (propiedades lowerCamelCase)
      const dto = resp.data;
      const adapted = {
        idTarea: dto.idTarea,
        observaciones: dto.observaciones,
        estado: dto.estadoNombre,
        areaNombre: dto.areaNombre,
        sedeNombre: dto.sedeNombre,
        servicioNombre: dto.servicioNombre,
        cargoUsuarioCliente: dto.cargoUsuarioCliente,
        urlEvidencia: dto.urlEvidencia,
        calificacion: dto.calificacion,
        fechaCreacion: dto.fechaCreacion,
      };
      setDetalleTarea(adapted);
    } else {
      setErrorDetalle(resp.message || 'No se pudo cargar la tarea.');
    }
    setLoadingDetalle(false);
  };

  // Columnas para detalle de tarea en tabla reusable (alineadas con /tareas)
  const columnsDetalle = [
    { key: 'idTarea', label: 'ID Tarea' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'estado', label: 'Estado', formatter: (estado) => estado || '' },
    { key: 'areaNombre', label: 'Area' },
    { key: 'sedeNombre', label: 'Sede' },
    { key: 'servicioNombre', label: 'Servicio' },
    { key: 'cargoUsuarioCliente', label: 'Cargo' },
    { key: 'urlEvidencia', label: 'Evidencia', formatter: (url) => url ? (<a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline' }}>Ir a evidencia</a>) : '' },
    { key: 'calificacion', label: 'Calificación', formatter: (cal) => cal === null ? 'No se ha calificado' : cal },
    { key: 'fechaCreacion', label: 'Fecha Creación', formatter: (date) => formatearBogota(date) },
    {
      key: 'acciones',
      label: 'Acciones',
      formatter: (value, row) => (
        <>
          {row?.idTarea ? <ComentarioButton tareaId={row.idTarea} /> : null}
        </>
      )
    }
  ];

  const links = [
    { to: '/home', label: 'Home' },
    { to: '/crear-tarea', label: 'Crear Tarea' },
    { to: '/tareas', label: 'Ver Tarea' },
    { to: '/cambiar-contrasena', label: 'Cambiar Contraseña' },
  ];

  // Si el usuario es admin, añade el enlace al panel de administración
  if (esAdmin()) {
    links.push({ to: '/admin', label: 'Admin Panel' });
  }

  // Si el usuario es de soporte técnico, añade el enlace de soporte
  if (esSoporteTecnico()) {
    links.push({ to: '/soporte', label: 'Soporte' });
  }

  // Calcular tareas pendientes (totalTareas - tareasEstadoCero)
  const tareasPendientes = estadisticas.totalTareas - estadisticas.tareasEstadoFinalizado;

  // Columnas para la tabla de tareas calificables
  const columnsCalificables = [
    { key: 'idTarea', label: 'ID Tarea' },
    { key: 'observaciones', label: 'Observaciones' },
    { key: 'areaNombre', label: 'Area' },
    { key: 'sedeNombre', label: 'Sede' },
    { key: 'cargoUsuarioCliente', label: 'Cargo' },
    {
      key: 'fechaCreacion',
      label: 'Fecha Creación',
      formatter: (date) => formatearBogota(date)
    },
    {
      key: 'calificacion',
      label: 'Calificación',
      formatter: (calificacion) => calificacion === null ? 'No se ha calificado' : calificacion
    },
    {
      key: 'acciones',
      label: 'Acciones',
      formatter: (value, row) => (
        <CalificarTareaButton tareaId={row.idTarea} />
      )
    }
  ];

  return (
    <div className="home-container">
      <Sidebar links={links} onLogout={handleLogout} />
      <main className="home-content">
        <h1>Bienvenido {userName}</h1>

        {loading && <p>Cargando estadísticas...</p>}
        {error && <p style={{ color: 'red' }}>{error}</p>}

        <div className="boxes-container">
          <div className="box-wrapper">
            <div className="info-box box-hechas">
              {!loading && !error && tareasPendientes}
            </div>
            <p className="box-label">Pendientes</p>
          </div>
          <div className="box-wrapper">
            <div className="info-box box-todas">
              {!loading && !error && estadisticas.totalTareas}
            </div>
            <p className="box-label">Todas</p>
          </div>
        </div>

        <div className="buttons-container">
          <button 
            className="iphone-button btn-comentarios"
            onClick={handleTareasConComentarios}
            disabled={loadingComentarios}
          >
            {loadingComentarios ? 'Cargando...' : 'Tareas con Comentarios'}
          </button>
          <button 
            className="iphone-button btn-calificables"
            onClick={() => setShowModalCalificables(true)}
          >
            Calificables
          </button>
          <button className="iphone-button btn-crear" onClick={() => navigate('/crear-tarea')}>
            Crear +
          </button>
        </div>
      </main>

      {/* Modal de Tareas Calificables */}
      {showModalCalificables && (
        <Modal
          title="Tareas Calificables"
          onClose={() => setShowModalCalificables(false)}
        >
          <ReusableTable
            data={(estadisticas.tareasConEstadoFinalizado || []).slice().sort((a, b) => b.idTarea - a.idTarea)}
            columns={columnsCalificables}
            keyField="idTarea"
          />
        </Modal>
      )}

      {/* Modal de Tareas con Comentarios */}
      {showModalComentarios && (
        <Modal
          title="Tareas con Comentarios No Leídos"
          onClose={() => setShowModalComentarios(false)}
        >
          {loadingComentarios ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>Cargando tareas con comentarios...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p style={{ color: '#dc2626', marginBottom: '15px' }}>Error: {error}</p>
              <button 
                onClick={handleTareasConComentarios}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Reintentar
              </button>
            </div>
          ) : tareasConComentarios.length > 0 ? (
            <div>
              <p style={{ marginBottom: '20px', fontSize: '16px', color: '#666' }}>
                Se encontraron {tareasConComentarios.length} tarea(s) con comentarios no leídos:
              </p>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', 
                gap: '10px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {tareasConComentarios.map((idTarea) => (
                  <div 
                    key={idTarea}
                    style={{
                      padding: '15px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '8px',
                      textAlign: 'center',
                      border: '2px solid #dc2626',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleClickTareaId(idTarea)}
                  >
                    <strong style={{ fontSize: '18px', color: '#dc2626' }}>
                      Tarea #{idTarea}
                    </strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      ID de tarea
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ 
                marginTop: '20px', 
                fontSize: '14px', 
                color: '#888', 
                fontStyle: 'italic',
                textAlign: 'center'
              }}>
                Estas son las tareas que tienen comentarios no leídos
              </p>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>No hay tareas con comentarios no leídos.</p>
            </div>
          )}
        </Modal>
      )}

      {/* Modal de Detalle de Tarea */}
      {showModalDetalleTarea && (
        <Modal
          title={detalleTarea ? `Detalle Tarea #${detalleTarea.idTarea}` : 'Detalle de Tarea'}
          onClose={() => setShowModalDetalleTarea(false)}
        >
          {loadingDetalle ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>Cargando detalle...</p>
            </div>
          ) : errorDetalle ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p style={{ color: '#dc2626' }}>{errorDetalle}</p>
            </div>
          ) : detalleTarea ? (
            <ReusableTable
              data={[detalleTarea]}
              columns={columnsDetalle}
              keyField={'idTarea'}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>Sin datos para mostrar.</p>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default HomePage; 